import { isEqual } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Footer, openModal } from "risa-oasis-ui_v2";
import { LoaderMessage } from "../../components/loaderMessage/loaderMessage";
import ApproveEvbv from "../../components/modals/approveEvbv/approveEvbv";
import BoValuesModal from "../../components/modals/BoValuesModal/BoValuesModal";
import ImageviewerModal from "../../components/modals/imageViewerModal/imageviewerModal";
import ImageViewerTimestamp from "../../components/modals/imageViewerModal/imageViewerTimestamp";
import SingleTab from "../../components/singleTab/singleTab";
import { useBusniessOfficeNotesContext } from "../../context/busniessOfficeNotesContext";
import { usePayerNameContextFromSingleOrderLayout } from "../../context/payerNameContextFromSingleOrderLayout";
import { JCodeDetails } from "../../data-model/medicalPaOrdersModel";
import { AuthOnFileRpaStatus } from "../../enums/authOnFileAuthStatus";
import { BusniessOfficeReportType } from "../../enums/busniessOfficeReportType";
import {
  DocWriteBackStatus,
  EVWriteBackStatus,
} from "../../enums/evBvWriteBackStatus";
import {
  ModalNamesForBigQuery,
  ScreenNamesForBigQuery,
} from "../../enums/eventsData";
import { EventsName } from "../../enums/eventsName";
import { LocalStorageKeys } from "../../enums/localStorageKeys";
import { MedicalPaOrdersAuthStatus } from "../../enums/medicalPaOrdersAuthStatus";
import {
  financialStatusOptions,
  MedicalPaOrderStatus,
} from "../../enums/medicalPaOrderStatus";
import { ModalId } from "../../enums/modalId";
import { fetchFormConfigfromFirebaseWithoutLiveListen } from "../../redux/slice/formConfigNoLiveListenSlice";
import { fetchMedicalPaClinicalData } from "../../redux/slice/medicalPa/medicalPaClinicalDataSlice";
import { fetchSingleMedicalPaFormConfig } from "../../redux/slice/medicalPa/singleMedicalPaFormConfig";
import { getMedicalPaOrderDetailsFor } from "../../redux/slice/medicalPa/singleMedicalPaOrder";
import { getMedicalPaAuthorization } from "../../redux/slice/medicalPaAuthorizationSlice";
import { closeModal, setOpenedModalId } from "../../redux/slice/modalSlice";
import { fetchWorkflowConfigDataForAnOrder } from "../../redux/slice/workflowConfigSlice";
import { AppDispatch, RootState } from "../../redux/store/store";
import { getLowestPriorityOption } from "../../utils/busniessOfficeNotesHelper";
import { determineIfDocumentRpaShouldRun } from "../../utils/determineIfDocumentRpaShouldRun";
import { generateFullName } from "../../utils/generateOrderDataForTable";
import { getAuthStatusOfMedicalPaOrder } from "../../utils/getAuthStatusOfMedicalPaOrder";
import { rotateLastSegmentFirst } from "../../utils/getFullNameOfTheProvider";
import {
  getItemFromLocalStorage,
  setItemInLocalStorage,
} from "../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../utils/organizationHelper";
import { capitalizeWordsSeperatedByUnderScore } from "../../utils/stringModifications";
import { apiWorkerManager } from "../../workerManagers/workerManager";
import { generatePrimaryDetails } from "../worklists/singleOrder/utils/singleOrderPageDataGenerator";
import RpaError from "./components/errorComponents/rpaError";
import { RpaStatusBanner } from "./components/errorComponents/rpaStatusBanner";
import HeaderSingleOrder from "./components/header/header";
import MultiplePastRegimenBadge from "./components/multiplePastRegimenBadge";
import { useAuthOnFileRefresh } from "./hooks/useAuthOnFileRefresh";
import { useDraftSubmissionIsPresent } from "./hooks/useDraftSubmissionIsPresent";
import { useIfMultiplePastRegimenExists } from "./hooks/useIfMultiplePastRegimenExists";
import { useShouldStartFilingAndButtonText } from "./hooks/useShouldStartFilingAndButtonText";
import { useSubmissionIsPresent } from "./hooks/useSubmissionIsPresent";
import { checkRpaError, checkStatus } from "./utils/checkRpaError";
import { clearDataFromLocalStorage } from "./utils/clearDataFromLocalStorage";
import { createBusniessOfficeNotesData } from "./utils/createBusniessOfficeNoteData";
import {
  getAllJCodes,
  saveExistingJCodeDocsToLocalStorage,
} from "./utils/existingDocsToLs";
import { saveJCodeDocsToLocalStorage } from "./utils/saveJCodeDocstoLS";

const SingleOrderLayout = (props: {
  setShowCigNoteModal?: (show: boolean) => void;
}) => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { singleMedicalPaOrder, isLoading, error, unsubscribe } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );
  const { unsubscribe: medicalPaClinicalDataUnsubscribe } = useSelector(
    (state: RootState) => state.medicalPaClinicalData,
  );
  const { data: workflowConfig, isLoading: isWorkflowConfigLoading } =
    useSelector((state: RootState) => state.workflowConfig);
  const { formConfigs, loading: isFormConfigLoading } = useSelector(
    (state: RootState) => state.formConfigNoLiveListen,
  );

  const { openedModalId } = useSelector((state: RootState) => state.modal);
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );
  const { data: authStatusOptions } = useSelector(
    (state: RootState) => state.authStatusOptions,
  );

  const [isApproveEvbvModalOpen, setIsApproveEvbvModalOpen] =
    React.useState<boolean>(false);
  const {
    setAllCommentsForUI,
    setGlobalBoValue,
    allCommentsForUI,
    setOrderId,
    setAuthStatus,
    setManualAuthStatus,
    setRawData,
    rawData,
    orderId: busniessOfficeNotesOrderId,
    setShouldDisableMarkAsComplete,
  } = useBusniessOfficeNotesContext();
  const draftSubmissionIsPresent = useDraftSubmissionIsPresent(
    orderId ?? "",
    singleMedicalPaOrder ?? {},
  );
  const submissionIsPresent = useSubmissionIsPresent();
  const shouldShowStartFilingButtonAndText = useShouldStartFilingAndButtonText(
    orderId ?? "",
    singleMedicalPaOrder ?? {},
  );
  const ifMultiplePastRegimenExists = useIfMultiplePastRegimenExists();

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );

  const { data: boOptions } = useSelector(
    (state: RootState) => state.boOptions,
  );

  const { payerName } = usePayerNameContextFromSingleOrderLayout();

  const hasLoggedToBigQuery = useRef<boolean>(false);

  const orgId = getOrgIdForFetchExternalWorklist();

  const [imageViewerModalOpen, setImageViewerModalOpen] = useState(false);
  const [isMounted, setIsMounted] = useState<boolean>(false);

  const { handleAuthOnFileRefresh } = useAuthOnFileRefresh({
    orderId: orderId ?? "",
    patientMrn: singleMedicalPaOrder?.payload?.patient_id,
    drugName: singleMedicalPaOrder?.payload?.regimen_name,
  });

  // Detect current tab and Medical Necessity flow status
  const location = useLocation();
  const isOnCptCodesTab = location.pathname.includes("/cptCodes");

  const shouldShowMedicalNecessityFlow = useMemo(() => {
    // Check both locations: status.bo_status (Firestore) and bo_status (Algolia/top-level)
    const boValue =
      singleMedicalPaOrder?.status?.bo_status ||
      (singleMedicalPaOrder as any)?.bo_status;
    return boValue?.toLowerCase() === "required"; // case-insensitive check
  }, [singleMedicalPaOrder]);

  const shouldShowContinueToMedicalNecessity =
    isOnCptCodesTab && shouldShowMedicalNecessityFlow;

  // Show footer on all tabs including CPT Codes (removed CPT Codes-specific footer)
  const shouldHideFooter = false;

  useEffect(() => {
    if (isApproveEvbvModalOpen) {
      openModal("approve-evbv-modal");
    }
  }, [isApproveEvbvModalOpen]);

  useEffect(() => {
    if (openedModalId === ModalId.BO_VALUES_MODAL) {
      openModal(ModalId.BO_VALUES_MODAL);
    }
  }, [openedModalId]);

  useEffect(() => {
    if (imageViewerModalOpen) {
      openModal("image-viewer-modal");
    }
  }, [imageViewerModalOpen]);

  const [primaryDetails, setPrimaryDetails] = useState<
    | {
        headerText: string;
        subText: string;
      }[]
    | null
  >(null);

  const [regimenData, setRegimenData] = useState<string[]>([]);

  useEffect(() => {
    if (orderId) {
      dispatch(getMedicalPaOrderDetailsFor(orderId));
      dispatch(fetchWorkflowConfigDataForAnOrder(orderId));
      dispatch(fetchFormConfigfromFirebaseWithoutLiveListen());
      dispatch(fetchMedicalPaClinicalData(orderId));
      dispatch(getMedicalPaAuthorization(orderId));
    }
    return () => {
      if (unsubscribe) unsubscribe();
      if (medicalPaClinicalDataUnsubscribe) medicalPaClinicalDataUnsubscribe();
    };
  }, [orderId, dispatch]);

  useEffect(() => {
    if (singleMedicalPaOrder) {
      const jCodes = getAllJCodes(singleMedicalPaOrder) as JCodeDetails[];

      if (jCodes && jCodes.length > 0) {
        let doesAnyJCodeHaveNotAvailableStatus = jCodes?.some(
          (jCode) => jCode.auth_status === "not_available",
        );
        if (doesAnyJCodeHaveNotAvailableStatus) {
          setShouldDisableMarkAsComplete(true);
        } else {
          setShouldDisableMarkAsComplete(false);
        }
      }
    }
  }, [singleMedicalPaOrder]);

  useEffect(() => {
    return () => {
      clearDataFromLocalStorage(orderId ?? "");
    };
  }, []);

  useEffect(() => {
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [unsubscribe]);

  const checkIfSubmissionPageShouldBeShown = () => {
    const getAuthStatus = getAuthStatusOfMedicalPaOrder(
      singleMedicalPaOrder ?? {},
      authStatusOptions,
    ).id;

    if (getAuthStatus === MedicalPaOrdersAuthStatus.AuthOnFile) {
      return false;
    }

    if (getAuthStatus === MedicalPaOrdersAuthStatus.AuthRequired) {
      if (
        singleMedicalPaOrder?.status?.submission === undefined ||
        singleMedicalPaOrder?.status?.submission === null ||
        singleMedicalPaOrder?.status?.submission ===
          MedicalPaOrderStatus.PAYER_NOT_SUPPORTED
      ) {
        return false;
      }
      return true;
    }

    return false;
  };

  const getCurrentStatusBadge = useMemo(() => {
    const currentStatus =
      singleMedicalPaOrder?.status?.financial_review ??
      MedicalPaOrderStatus.NEW;
    const currentStatusBadge = financialStatusOptions.find(
      (option) => option.id === currentStatus,
    );
    return currentStatusBadge;
  }, [singleMedicalPaOrder]);

  useEffect(() => {
    if (singleMedicalPaOrder && !isLoading && !error) {
      setIsMounted(true);
      setPrimaryDetails(generatePrimaryDetails(singleMedicalPaOrder));
      saveJCodeDocsToLocalStorage(singleMedicalPaOrder);
      const jCodes = getAllJCodes(singleMedicalPaOrder);

      // Log to BigQuery only once when data is first loaded
      if (!hasLoggedToBigQuery.current) {
        apiWorkerManager
          .enqueueAndProcess({
            patient_id: singleMedicalPaOrder?.patient_id ?? "",
            order_id: singleMedicalPaOrder?.order_id ?? "",
            org_id: orgId ?? "",
            event_name: EventsName.AUTH_STATUS_SCREEN_SHOWN,
            user_id: user?.id ?? "",
            user_email: user?.email ?? "",
            additional_data: {
              screen_name: ScreenNamesForBigQuery.WORKLIST,
              source: ScreenNamesForBigQuery.WORKLIST,
              order_status:
                singleMedicalPaOrder?.status?.financial_review ?? "",
              auth_status:
                singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
              drug_name: singleMedicalPaOrder?.payload?.regimen_name ?? "",
              payer_name: payerName,
              j_codes:
                jCodes?.map((jCode) => jCode?.j_code ?? "").join(",") ?? "",
              drugs:
                singleMedicalPaOrder?.hpcs_codes
                  ?.map((hpcs) => hpcs.description ?? "")
                  .join(",") ?? "",
            },
          })
          .catch((error) => {});

        hasLoggedToBigQuery.current = true;
      }
      if (singleMedicalPaOrder && !isMounted) {
        const allDocs =
          saveExistingJCodeDocsToLocalStorage(singleMedicalPaOrder);
        setItemInLocalStorage(
          `${LocalStorageKeys.EXISTING_JCODE_DOCS}_${orderId}`,
          allDocs,
        );
        setIsMounted(true);
      }
    }
  }, [singleMedicalPaOrder, isLoading, error, boOptions]);

  useEffect(() => {
    if (primaryDetails) {
      const regimenDetail = primaryDetails.find(
        (detail) => detail.headerText === "Regimen",
      );
      if (regimenDetail) {
        setRegimenData([regimenDetail.subText]);
      }
    }
  }, [primaryDetails, singleMedicalPaOrder]);

  const updateBusinessOfficeNotesContext = () => {
    if (!singleMedicalPaOrder) return;

    const busniessOfficeNotes =
      singleMedicalPaOrder?.auth_on_file?.reports ?? [];
    const isSameOrderId =
      busniessOfficeNotesOrderId === singleMedicalPaOrder?.id;
    const isSameBusniessOfficeNotes = isEqual(
      rawData ?? [],
      busniessOfficeNotes ?? [],
    );

    if (!isSameOrderId || !isSameBusniessOfficeNotes) {
      const templateText =
        singleMedicalPaOrder?.auth_on_file?.template_text ?? "";
      setAllCommentsForUI(
        createBusniessOfficeNotesData(
          busniessOfficeNotes,
          templateText,
          singleMedicalPaOrder?.payload?.date_of_service,
        ),
      );
      setOrderId(singleMedicalPaOrder?.id ?? "");
      setAuthStatus(singleMedicalPaOrder?.auth_on_file?.auth_status ?? "");
      if (!isSameOrderId) {
        setManualAuthStatus("");
      }
      setRawData(busniessOfficeNotes ?? []);
    }
  };

  useEffect(() => {
    updateBusinessOfficeNotesContext();
  }, [singleMedicalPaOrder]);

  useEffect(() => {
    const getGlobalBoValue = () => {
      let globalBoValue = "";
      const finalComments = allCommentsForUI.filter(
        (comment) => comment.type === BusniessOfficeReportType.FINAL,
      );
      if (finalComments.length === 1) {
        const comments = finalComments[0].comments;
        if (comments.length === 1) {
          globalBoValue = comments[0].boStatus || "";
        } else if (comments.length > 1) {
          const allBoStatuses = comments.map(
            (comment) => comment?.boStatus ?? "",
          );
          const lowestPriorityOption = getLowestPriorityOption(
            boOptions,
            allBoStatuses ? allBoStatuses : [],
          );
          globalBoValue = lowestPriorityOption?.value || "";
        }
      }
      return globalBoValue;
    };
    const newGlobalBoValue = getGlobalBoValue();
    setGlobalBoValue(newGlobalBoValue);
    setItemInLocalStorage(
      `${LocalStorageKeys.GLOBAL_BO_VALUE}_${orderId}`,
      newGlobalBoValue,
    );
  }, [allCommentsForUI, boOptions, orderId]);

  const tabSwitchElements = useMemo(() => {
    const allElements = [
      { id: "cptCodes", label: "Drugs" },
      { id: "comments", label: "Business Office" },
      { id: "visitData", label: "Visit Data Chart" },
      { id: "documents", label: "Documents" },
      { id: "authLetters", label: "Auth Letters" },
    ];

    return allElements;
  }, []);

  const onBackClick = () => {
    navigate(`/worklists/evbv/${orderId}`);
  };

  const logOnTabClickEvent = (label: string) => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.AUTH_TAB_SELECTED + " (" + label + ")",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        tab_selected: label,
        payer_name: payerName,
      },
    });
  };

  const hideButton = useMemo(() => {
    if (providerDetails?.IsAdmin) return false;

    if (
      singleMedicalPaOrder?.assigned_to?.provider_id ===
        providerDetails?.DocID &&
      singleMedicalPaOrder?.assigned_to?.facility_id ===
        providerDetails?.FacilityId
    )
      return false;

    return true;
  }, [providerDetails, singleMedicalPaOrder]);

  useEffect(() => {
    if (imageViewerModalOpen) {
      openModal("image-viewer-modal");
    }
  }, [imageViewerModalOpen]);

  const buttonTextforMarkAsCompleted = useMemo(() => {
    const shiftMasterQueueStatus = [
      MedicalPaOrdersAuthStatus.Pending,
      MedicalPaOrdersAuthStatus.AuthRequired,
      MedicalPaOrdersAuthStatus.Query,
      MedicalPaOrdersAuthStatus.Hold,
    ];

    if (
      shiftMasterQueueStatus.includes(
        singleMedicalPaOrder?.auth_on_file
          ?.auth_status as MedicalPaOrdersAuthStatus,
      )
    ) {
      const authStatusLabel = capitalizeWordsSeperatedByUnderScore(
        singleMedicalPaOrder?.auth_on_file
          ?.auth_status as MedicalPaOrdersAuthStatus,
      );
      return `Shift to ${authStatusLabel} Queue`;
    }
    return "Mark as completed";
  }, [singleMedicalPaOrder?.auth_on_file?.auth_status]);

  const allMatchedFormConfigs = useMemo(() => {
    if (
      formConfigs &&
      workflowConfig &&
      workflowConfig?.submission_form_config_id !== ""
    ) {
      const allSubmissionFormConfigs =
        workflowConfig?.submission_form_config_id?.split(",");
      const allFormConfigs = formConfigs.filter((formConfig) =>
        allSubmissionFormConfigs?.includes(formConfig.id),
      );

      const dataToShow = allFormConfigs.map((formConfig) => {
        return {
          id: formConfig?.id,
          name: formConfig?.data?.formTitle ?? "",
        };
      });
      return dataToShow;
    }
    return [];
  }, [formConfigs, workflowConfig, singleMedicalPaOrder?.id]);

  const shouldOpenHappyModal = useMemo(() => {
    const evWriteBackStatusForHappyModal = [
      EVWriteBackStatus.SUCCESS,
      EVWriteBackStatus.IN_PROGRESS,
    ];
    const documentUploadStatusForHappyModal = [
      DocWriteBackStatus.SUCCESS,
      DocWriteBackStatus.IN_PROGRESS,
    ];
    const evStatus = singleMedicalPaOrder?.status?.ev_write_back_status;
    const docStatus = singleMedicalPaOrder?.status?.document_upload_status;

    if (!evStatus) return false;

    if (
      singleMedicalPaOrder &&
      determineIfDocumentRpaShouldRun(orderId ?? "")
    ) {
      if (!docStatus) return false;

      return (
        evWriteBackStatusForHappyModal.includes(
          evStatus as EVWriteBackStatus,
        ) &&
        documentUploadStatusForHappyModal.includes(
          docStatus as DocWriteBackStatus,
        )
      );
    }

    return evWriteBackStatusForHappyModal.includes(
      evStatus as EVWriteBackStatus,
    );
  }, [
    singleMedicalPaOrder?.status?.ev_write_back_status,
    singleMedicalPaOrder?.status?.document_upload_status,
    orderId,
  ]);

  const openCorrectModal = () => {
    const planName = singleMedicalPaOrder?.coverage?.primary?.plan_name ?? "";

    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.MARK_AS_COMPLETED_BUTTON_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        master_auth_status_old:
          singleMedicalPaOrder?.status?.master_auth_status ?? "",
        master_auth_status_new:
          singleMedicalPaOrder?.auth_on_file?.auth_status ?? "",
        payer_name: payerName,
        plan_name: planName,
        j_codes: (
          getItemFromLocalStorage(
            `${LocalStorageKeys.J_CODES}_${singleMedicalPaOrder?.id}`,
          ) ?? []
        ).join(","),
      },
    });
    if (shouldOpenHappyModal) {
      dispatch(setOpenedModalId({ id: ModalId.HAPPY_FLOW_MODAL }));
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgId ?? "",
        event_name: EventsName.HAPPY_FLOW_MODAL_OPENED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
          modal_name: ModalNamesForBigQuery.HAPPY_FLOW_MODAL,
          payer_name: payerName,
        },
      });
    } else {
      dispatch(setOpenedModalId({ id: ModalId.COMMENT_RPA_MODAL_NEW }));
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgId ?? "",
        event_name: EventsName.COMMENT_RPA_MODAL_NEW_OPENED,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
          modal_name: ModalNamesForBigQuery.COMMENT_RPA_MODAL_NEW,
          payer_name: payerName,
        },
      });
    }
  };

  const handleStartFilingClick = () => {
    apiWorkerManager.enqueueAndProcess({
      patient_id: singleMedicalPaOrder?.patient_id ?? "",
      order_id: singleMedicalPaOrder?.order_id ?? "",
      org_id: orgId ?? "",
      event_name: EventsName.START_FILING_CLICKED,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      additional_data: {
        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        source: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
        patient_name: generateFullName(
          singleMedicalPaOrder?.demographics?.first_name ?? "",
          "",
          singleMedicalPaOrder?.demographics?.last_name ?? "",
        ),
        patient_mrn: singleMedicalPaOrder?.demographics?.patient_mrn ?? "",
        coverage_type:
          singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
        payer_name: payerName,
      },
    });
    if (draftSubmissionIsPresent) {
      navigate(`/worklists/draft-submission/${orderId}`);
      return;
    }
    if (submissionIsPresent) {
      navigate(`/worklists/submission-status/${orderId}`);
      return;
    }
    if (allMatchedFormConfigs && allMatchedFormConfigs.length > 1) {
      apiWorkerManager.enqueueAndProcess({
        patient_id: singleMedicalPaOrder?.patient_id ?? "",
        order_id: singleMedicalPaOrder?.order_id ?? "",
        org_id: orgId ?? "",
        event_name: EventsName.MODAL_SHOWN,
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
          source: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
          modal_name: ModalNamesForBigQuery.FORM_SELECTION_MODAL,
          payer_name: payerName,
        },
      });
      dispatch(
        setOpenedModalId({
          id: ModalId.FORM_SELECTION_MODAL,
          metaData: {
            formConfigs: allMatchedFormConfigs,
            payerName:
              singleMedicalPaOrder?.coverage?.primary?.payer_name ?? "",
            orderId: singleMedicalPaOrder?.order_id ?? "",
          },
        }),
      );
    } else {
      dispatch(
        fetchSingleMedicalPaFormConfig(allMatchedFormConfigs[0].id ?? ""),
      );
      navigate(`/worklists/medical-pa-form/${singleMedicalPaOrder?.id ?? ""}`);
    }
  };

  return (
    <div className="single-order--layout flex h-full w-full flex-col gap-3 !p-0">
      {isLoading || isWorkflowConfigLoading || isFormConfigLoading ? (
        <LoaderMessage message="Fetching Order Details..." />
      ) : error ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-4">
          <div className="text-h16 font-bold text-red-500">Error</div>
          <div className="text-h14 text-gray-600">{error}</div>
          <button
            onClick={onBackClick}
            className="rounded-md bg-tertiaryBlue-4 px-4 py-2 text-white hover:bg-tertiaryBlue-5"
          >
            Go Back
          </button>
        </div>
      ) : (
        <>
          <div className="single-order-layout--content flex h-full w-full flex-col gap-3 overflow-y-auto rounded-md bg-white px-4 pb-1 pt-2">
            <div className="single-order-layout--content--header">
              <HeaderSingleOrder
                patientName={
                  singleMedicalPaOrder?.demographics?.first_name +
                  " " +
                  singleMedicalPaOrder?.demographics?.last_name
                }
                patientMrn={singleMedicalPaOrder?.demographics?.patient_mrn}
                location={singleMedicalPaOrder?.payload?.location ?? "N/A"}
                authorizationId={"N/A"}
                dateOfService={singleMedicalPaOrder?.payload?.date_of_service}
                status={singleMedicalPaOrder?.auth_on_file?.auth_status}
                orderId={singleMedicalPaOrder?.order_id ?? ""}
                hideButton={hideButton}
                orderData={singleMedicalPaOrder}
                providerName={rotateLastSegmentFirst(
                  singleMedicalPaOrder?.payload?.practitioner_name ?? "",
                )}
                insuranceName={
                  singleMedicalPaOrder?.coverage?.primary?.payer_name ?? ""
                }
              />
            </div>
            <div className="mt-1 flex items-center justify-between bg-yellow-50 p-3 text-gray-600">
              <div className="text-xs">{regimenData}</div>
              <div className="action-container-with-multiple-past-regimen-flag flex items-center gap-3">
                {ifMultiplePastRegimenExists && <MultiplePastRegimenBadge />}
                <div
                  className="cursor-pointer text-sm font-bold text-tertiaryBlue-4 hover:cursor-pointer hover:text-tertiaryBlue-5"
                  onClick={() => {
                    setImageViewerModalOpen(true);
                    apiWorkerManager.enqueueAndProcess({
                      patient_id: singleMedicalPaOrder?.patient_id ?? "",
                      order_id: singleMedicalPaOrder?.order_id ?? "",
                      org_id: orgId ?? "",
                      event_name: EventsName.VIEW_TX_PLAN_CLICKED,
                      user_id: user?.id ?? "",
                      user_email: user?.email ?? "",
                      additional_data: {
                        screen_name: ScreenNamesForBigQuery.AUTH_STATUS_SCREEN,
                        payer_name: payerName,
                      },
                    });
                  }}
                >
                  View Tx Plan
                </div>
              </div>
            </div>
            {checkRpaError(singleMedicalPaOrder ?? {}) ? (
              <RpaError onRetry={handleAuthOnFileRefresh} />
            ) : (
              <div className="single-order-layout--content flex h-full w-full flex-col gap-3">
                <div className="single-order-layout--content--secondary__left flex h-full flex-col">
                  <div className="single-order-layout--content--secondary__left--navigation h-6 border-b border-primaryGray-15 pb-9">
                    {tabSwitchElements.map((sideNavElement) => (
                      <SingleTab
                        key={sideNavElement.id}
                        id={sideNavElement.id}
                        label={sideNavElement.label}
                        logEvent={logOnTabClickEvent}
                      />
                    ))}
                  </div>
                  {checkStatus(singleMedicalPaOrder ?? {}) ===
                    AuthOnFileRpaStatus.IN_PROGRESS ||
                  checkStatus(singleMedicalPaOrder ?? {}) ===
                    AuthOnFileRpaStatus.INITIATED ? (
                    <RpaStatusBanner orderData={singleMedicalPaOrder ?? {}} />
                  ) : (
                    <div className="single-order-layout--content--secondary__left--outlet mt-4 h-full">
                      <Outlet />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          {/* Footer - hidden on CPT Codes tab as it renders its own */}
          {!shouldHideFooter && (
            <div className="single-order-layout--footer">
              <Footer
                cancelButtonText={
                  shouldShowContinueToMedicalNecessity
                    ? "Go Back"
                    : shouldShowStartFilingButtonAndText.startFilingButtonText
                }
                submitButtonText={
                  shouldShowContinueToMedicalNecessity
                    ? "Continue"
                    : buttonTextforMarkAsCompleted
                }
                isDisabled={false}
                onCancelClick={
                  shouldShowContinueToMedicalNecessity
                    ? onBackClick
                    : shouldShowStartFilingButtonAndText.shouldShowStartFilingButton
                      ? handleStartFilingClick
                      : onBackClick
                }
                onSubmitClick={
                  shouldShowContinueToMedicalNecessity
                    ? () =>
                        navigate(
                          `/worklists/medical-necessity-review/${orderId}`,
                        )
                    : () => openCorrectModal()
                }
                children={
                  <>
                    {shouldShowStartFilingButtonAndText.shouldShowStartFilingButton &&
                      !shouldShowContinueToMedicalNecessity && (
                        <Button
                          buttonType={"tertiary"}
                          size={"medium"}
                          onClick={onBackClick}
                          disabled={false}
                          children={"Go Back"}
                        />
                      )}
                  </>
                }
              />
            </div>
          )}
        </>
      )}
      {isApproveEvbvModalOpen && (
        <ApproveEvbv
          onClose={() => setIsApproveEvbvModalOpen(false)}
          badgeList={financialStatusOptions.sort((a, b) =>
            a.id.localeCompare(b.id),
          )}
          selectedBadge={getCurrentStatusBadge}
          orderId={singleMedicalPaOrder?.id ?? ""}
          shouldNavigateToSubmission={checkIfSubmissionPageShouldBeShown()}
        />
      )}

      {openedModalId === ModalId.BO_VALUES_MODAL && (
        <BoValuesModal
          onClose={() => {
            dispatch(closeModal());
          }}
        />
      )}
      {imageViewerModalOpen && (
        <ImageviewerModal
          fileUrls={[]}
          onClose={() => {
            setImageViewerModalOpen(false);
            dispatch(closeModal());
          }}
          filePaths={
            singleMedicalPaOrder?.auth_on_file?.screenshots?.treatment_plan_path
              ? [
                  singleMedicalPaOrder?.auth_on_file?.screenshots
                    ?.treatment_plan_path,
                ]
              : []
          }
        >
          <ImageViewerTimestamp
            createdAt={
              singleMedicalPaOrder?.auth_on_file?.screenshots?.created_at
            }
          />
        </ImageviewerModal>
      )}
    </div>
  );
};
export default SingleOrderLayout;
